import './CoralTabs.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import React, { useLayoutEffect, useRef, useState } from 'react'
import Select, { components as selectComponents } from 'react-select'

type Tab = {
  label: string
}

type CoralTabsProps = {
  tabs: Tab[]
  activeIndex: number
  onTabChange: (index: number) => void
}

export const CoralTabs: React.FC<CoralTabsProps> = ({ tabs, activeIndex, onTabChange }) => {
  const isTablet = useIsWindowBiggerThan(719)
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({})
  const [isResizing, setIsResizing] = useState(false)

  const selectOptions = tabs.map((tab, index) => ({
    label: tab.label,
    value: index,
  }))
  useLayoutEffect(() => {
    if (!isTablet) return
    let resizeTimeout: number

    function updateHighlight() {
      const node = tabRefs.current[activeIndex]
      if (node) {
        setHighlightStyle({
          width: `${node.offsetWidth}px`,
          left: `${node.offsetLeft}px`,
        })
      }
    }

    function onResize() {
      setIsResizing(true)
      updateHighlight()
      clearTimeout(resizeTimeout)
      resizeTimeout = window.setTimeout(() => {
        setIsResizing(false)
      }, 100) // delay to detect resize end
    }

    updateHighlight()
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(resizeTimeout)
    }
  }, [activeIndex, isTablet, tabs.length])

  useLayoutEffect(() => {
    if (!isTablet) return
    const node = tabRefs.current[activeIndex]
    if (node) {
      setHighlightStyle({
        width: `${node.offsetWidth}px`,
        left: `${node.offsetLeft}px`,
      })
    }
  }, [activeIndex, isTablet, tabs.length])

  const handleSelectChange = (selectedOption: any) => {
    if (selectedOption) {
      onTabChange(selectedOption.value)
    }
  }
  const filteredOptions = selectOptions.filter((option) => option.value !== activeIndex)
  const highlightStyleWithTransition = {
    ...highlightStyle,
    transition: isResizing
      ? 'none'
      : 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  }

  return (
    <div className={'coral-tabs'}>
      {!isTablet ? (
        <Select
          aria-label={'Tab dropdown'}
          className={'coral-tabs-select-container'}
          classNamePrefix={'coral-tabs-select'}
          components={{ DropdownIndicator: selectComponents.DropdownIndicator }}
          isSearchable={false}
          onChange={handleSelectChange}
          options={filteredOptions}
          styles={{
            singleValue: (provided, state) => ({
              ...provided,
              color: state.selectProps.menuIsOpen ? '#636363' : '#000000',
            }),
            dropdownIndicator: (provided, state) => ({
              ...provided,
              transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
              color: state.selectProps.menuIsOpen ? '#636363' : '#000000',
            }),
          }}
          value={selectOptions[activeIndex]}
        />
      ) : (
        <div className={'coral-tabs__header'} style={{ position: 'relative' }}>
          <div className={'coral-tabs__highlight'} style={highlightStyleWithTransition} />
          {tabs.map((tab, index) => (
            <div
              className={`coral-tabs__button${index === activeIndex ? ' m--active' : ''}`}
              key={index}
              onClick={() => onTabChange(index)}
              ref={(el) => (tabRefs.current[index] = el)}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
